import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  ResponsiveContainer,
  BarChart,
  ComposedChart,
  Bar,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Label,
  Line,
} from "recharts";

// ───────────────────── Custom Native Markdown Renderer ─────────────────────
const NativeAnalysis = ({ content }) => {
  if (!content) return null;
  if (typeof content !== "string") return null;

  const parseInline = (text) => {
    const parts = text.split(/(\*\*.*?\*\*|_.*?_)/g);
    return parts.map((part, index) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return (
          <strong key={index} className="font-bold text-[#323334]">
            {part.slice(2, -2)}
          </strong>
        );
      } else if (part.startsWith("_") && part.endsWith("_")) {
        return (
          <em key={index} className="italic text-[#323334]">
            {part.slice(1, -1)}
          </em>
        );
      }
      return part;
    });
  };

  const lines = content.split("\n");

  return (
    <div className="font-sans text-[#7E8081] space-y-2 text-[15px] leading-[19.5px]">
      {lines.map((line, index) => {
        const trimmed = line.trim();
        if (!trimmed) return <div key={index} className="h-1"></div>;

        if (trimmed.startsWith("### "))
          return (
            <h3
              key={index}
              className="text-[16px] font-bold text-[#323334] mt-2 mb-1"
            >
              {parseInline(trimmed.slice(4))}
            </h3>
          );
        if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
          return (
            <div key={index} className="flex items-start ml-2 mb-1">
              <span className="mr-2 text-[#59AD31] text-lg leading-none">
                •
              </span>
              <span>{parseInline(trimmed.slice(2))}</span>
            </div>
          );
        }

        return (
          <p key={index} className="mb-2">
            {parseInline(trimmed)}
          </p>
        );
      })}
    </div>
  );
};

const LoadingAnalysisHelper = () => (
  <div className="flex items-center gap-2 text-[#7E8081] text-sm md:text-base animate-pulse">
    <svg className="animate-spin h-5 w-5 text-[#59AD31]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
    </svg>
    <span className="font-bold">Generating analysis...</span>
  </div>
);

function extractAnalysisText(resData, fallbackKeys = []) {
  if (!resData || typeof resData !== "object") {
    return "Could not load analysis at this time.";
  }

  if (typeof resData.analysis === "string" && resData.analysis.trim()) {
    return resData.analysis;
  }

  for (const key of fallbackKeys) {
    if (typeof resData[key] === "string" && resData[key].trim()) {
      return resData[key];
    }
  }

  return "Could not load analysis at this time.";
}

// ───────────────────── Helper functions (unchanged) ─────────────────────

// etiquetas eje Y
const yAxisLabelGHG = "MT CO2e";
const yAxisDxGHG = -yAxisLabelGHG.length;

const GHG_NUMERIC_KEYS = [
  "crops",
  "livestock",
  "deforestation",
  "otherEmissions",
  "sequestration",
  "netAfolu",
];

function toNumericGhgRow(item) {
  const row = { ...item };
  for (const key of GHG_NUMERIC_KEYS) {
    const n = Number(item?.[key]);
    row[key] = Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
  }
  return row;
}

function getTooltipIcon(entry, chartType = "bar") {
  const key = entry.dataKey;

  // rombo → netAfolu
  if (key === "netAfolu") {
    return (
      <svg width="10" height="10" viewBox="0 0 10 10">
        <polygon
          points="5,1 9,5 5,9 1,5"
          fill="#FFFFFF"
          stroke="#0F0F0F"
          strokeWidth="1"
        />
      </svg>
    );
  }

  // Line charts: SVG solid line
  if (chartType === "line") {
    return (
      <svg width="16" height="2" viewBox="0 0 16 2" className="inline">
        <line x1="0" y1="1" x2="16" y2="1" stroke={entry.color} strokeWidth="2" />
      </svg>
    );
  }

  // default → cuadradito de color
  return (
    <span
      className="h-2 w-2 rounded-[2px]"
      style={{ backgroundColor: entry.color }}
    />
  );
}

// ───────── tooltip custom (ordenado por valor) ─────────
function CustomTooltip({
  active,
  payload,
  label,
  chartType = "bar",
  hiddenKeys = [],
}) {
  if (!active || !payload || payload.length === 0) return null;

  const orderMap = Object.fromEntries(
    ghgLegendOrder.map((key, index) => [key, index])
  );

  const orderedPayload = [...payload]
    .filter((item) => !hiddenKeys.includes(item.dataKey))
    .sort(
      (a, b) =>
        (orderMap[a.dataKey] ?? 999) -
        (orderMap[b.dataKey] ?? 999)
    );

  return (
    <div className="rounded-lg border border-[#EDF0F2] bg-white px-3 py-2 text-[11px] shadow-md">
      <div className="mb-1 text-[11px] font-medium text-[#323334]">
        {label}
      </div>

      {orderedPayload.map((item) => (
        <div
          key={item.dataKey}
          className="mb-0.5 flex items-center justify-between gap-2"
        >
          <div className="flex items-center gap-1.5">
            {getTooltipIcon(item, chartType)}
            <span className="text-[#7E8081]">{item.name}</span>
          </div>

          <span className="font-medium text-[#323334]">
            {typeof item.value === "number"
              ? item.value.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              : item.value}{" "}
            <span className="text-[#98999A]">{yAxisLabelGHG}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

// orden fijo de la leyenda
const ghgLegendOrder = [
  "crops",
  "livestock",
  "deforestation",
  "otherEmissions",
  "sequestration",
  "netAfolu",
];

// ───────── dot custom ─────────
// Rombo (diamante) para Net Alfo
const diamondDot = ({ cx, cy }) => {
  if (cx == null || cy == null) return null;
  const size = 6;
  const points = [
    `${cx},${cy - size}`,
    `${cx + size},${cy}`,
    `${cx},${cy + size}`,
    `${cx - size},${cy}`,
  ].join(" ");
  return (
    <polygon
      points={points}
      fill="#FFFFFF"
      stroke="#0F0F0F"
      strokeWidth={1}
    />
  );
};

// ───────── leyenda custom ordenada y clicable ─────────
function GHGLegend({ payload, hiddenKeys, onToggle, chartType = "bar" }) {
  if (!payload) return null;

  const byKey = Object.fromEntries(payload.map((p) => [p.dataKey, p]));

  const ordered = ghgLegendOrder.map((key) => byKey[key]).filter(Boolean);

  return (
    <div className="mb-2 flex w-full flex-wrap items-center justify-center gap-4 text-[12px] text-[#98999A]">
      {ordered.map((entry) => {
        const isHidden = hiddenKeys.includes(entry.dataKey);
        const key = entry.dataKey;

        const isNet = key === "netAfolu";
        const isBar = [
          "crops",
          "livestock",
          "deforestation",
          "otherEmissions",
          "sequestration",
        ].includes(key);

        return (
          <button
            key={entry.dataKey}
            type="button"
            onClick={() => onToggle(entry)}
            className={`flex items-center gap-1.5 ${isHidden ? "opacity-40" : ""
              }`}
          >
            {isBar && (
              <span
                className="h-3 w-3 rounded-[2px]"
                style={{ backgroundColor: entry.color }}
              />
            )}

            {isNet && (
              <svg width="10" height="10" viewBox="0 0 10 10">
                <polygon
                  points="5,1 9,5 5,9 1,5"
                  fill="#FFFFFF"
                  stroke="#0F0F0F"
                  strokeWidth="1"
                />
              </svg>
            )}

            <span className="text-[12px]">
              {key === "netAfolu" ? "Net AFOLU" : entry.value}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// 2. Updated component to accept { data } prop
export default function GHGTab({ data, pathway, pathwayId, country, onLoadingChange }) {
  const [hiddenGHGKeys, setHiddenGHGKeys] = useState([]);
  const [ghgChartMode, setGhgChartMode] = useState("bar");
  const isGHGHidden = (key) => hiddenGHGKeys.includes(key);
  // 🔄 Limpiar estados cuando cambian los datos
  useEffect(() => {
    setHiddenGHGKeys([]);
  }, [data]);

  const chartData = useMemo(
    () =>
      data && Array.isArray(data["GHG by sector"])
        ? data["GHG by sector"].map(toNumericGhgRow)
        : [],
    [data],
  );

  const [ghgAnalysis, setGhgAnalysis] = useState("");
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);

  useEffect(() => {
    onLoadingChange?.(loadingAnalysis);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingAnalysis]);

  // ───────────────────── Data Fetching (LLM) ─────────────────────
  const fetchAnalysis = useCallback(async () => {
    if (chartData.length === 0 || !pathwayId || !country) {
      setGhgAnalysis("");
      setLoadingAnalysis(false);
      return;
    }

    setLoadingAnalysis(true);
    try {
      const payload = {
        analysisKey: "ghg_analysis",
        scope: "country",
        country: country,
        ghgData: {
          data: JSON.stringify(chartData),
          title: `GHG by sector - ${country}`,
        },
        context: "ghg",
        pathway,
        payload: {
          meta: {
            pathway_id: pathwayId,
            country: country,
            chart: "GHG by sector",
          },
        },
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/chat/fablegpt`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Analysis API error: ${response.status}`);
      }

      const resData = await response.json();
      setGhgAnalysis(extractAnalysisText(resData, ["ghgAnalysis", "analysis"]));
    } catch (error) {
      console.error("Error fetching analysis:", error);
      setGhgAnalysis("Cannot load analysis at this time.");
    } finally {
      setLoadingAnalysis(false);
    }
  }, [chartData, pathway, pathwayId, country]);

  useEffect(() => {
    fetchAnalysis();
  }, [fetchAnalysis]);

  const handleLegendClickGHG = (o) => {
    const { dataKey } = o;
    if (!dataKey) return;
    setHiddenGHGKeys((prev) =>
      prev.includes(dataKey)
        ? prev.filter((k) => k !== dataKey)
        : [...prev, dataKey]
    );
  };

  return (
    <div className="flex w-full flex-col gap-10">
      <section className="flex w-full flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <h2 className="text-[18px] font-medium text-[#323334]">
            GHG Emissions by source
          </h2>
          <div className="flex flex-wrap items-center gap-2 mt-2 sm:mt-0">
            <div className="flex rounded-full border border-[#D9DCDC] bg-white p-1 shadow-sm">
              {[
                { value: "bar", label: "Bar" },
                { value: "area", label: "Area" },
              ].map((mode) => {
                const active = ghgChartMode === mode.value;
                return (
                  <button
                    key={mode.value}
                    type="button"
                    onClick={() => setGhgChartMode(mode.value)}
                    className={`rounded-full px-3 py-1 text-sm transition ${active ? "bg-[#59AD31] text-white" : "text-[#7E8081] hover:bg-[#F4F7F6]"}`}
                  >
                    {mode.label}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              className="group flex items-center p-0 w-10 h-10 rounded-full bg-gradient-to-tr from-[#1E5B4F] to-[#C49A1F] text-white shadow-md hover:shadow-lg hover:scale-105 transition-all duration-200 overflow-hidden hover:w-44 text-center focus:outline-none focus:ring-2 focus:ring-[#C49A1F]"
              style={{ transitionProperty: 'width,background,color,box-shadow,transform', minWidth: '2.5rem', maxWidth: '11rem' }}
              aria-label="Download image"
              onClick={async () => {
                // Download image as JPG using html2canvas (chart only)
                const chartSection = document.getElementById('ghg-chart-section');
                if (!chartSection) return;
                const description = 'GHG Emissions by source';
                const html2canvas = (await import('html2canvas')).default;
                html2canvas(chartSection, { scale: 2, useCORS: true }).then((canvas) => {
                  canvas.toBlob((blob) => {
                    if (!blob) return;
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `${description.replace(/\s+/g, '_')}.jpg`;
                    link.click();
                    URL.revokeObjectURL(url);
                  }, 'image/jpeg', 0.95);
                });
              }}
            >
              <span className="relative w-10 h-10 transition-all duration-200">
                {/* Icono de imagen */}
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 flex-shrink-0 transition-all duration-200 group-hover:opacity-0 absolute inset-0 m-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <rect x="3" y="5" width="18" height="14" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
                  <circle cx="8.5" cy="10.5" r="1.5" fill="currentColor" />
                  <path d="M21 19l-5.5-7-4.5 6-3-4-4 5" stroke="currentColor" strokeWidth="2" fill="none" />
                </svg>
              </span>
              <span className="max-w-0 opacity-0 group-hover:max-w-[160px] group-hover:opacity-100 transition-all duration-200 whitespace-nowrap text-sm">Download image</span>
            </button>
            <button
              type="button"
              className="group flex items-center p-0 w-10 h-10 rounded-full bg-gradient-to-tr from-[#59AD31] to-[#C49A1F] text-white shadow-md hover:shadow-lg hover:scale-105 transition-all duration-200 overflow-hidden hover:w-44 text-center focus:outline-none focus:ring-2 focus:ring-[#59AD31]"
              style={{ transitionProperty: 'width,background,color,box-shadow,transform', minWidth: '2.5rem', maxWidth: '11rem' }}
              aria-label="Download data"
              onClick={async () => {
                // Download data as CSV using papaparse
                const description = 'GHG Emissions by source';
                const Papa = (await import('papaparse')).default;
                if (!chartData || !Array.isArray(chartData) || chartData.length === 0) return;
                const headers = Object.keys(chartData[0]);
                const csvRows = [
                  [`Description: ${description}`],
                  [],
                  headers,
                  ...chartData.map(row => headers.map(h => row[h]))
                ];
                const csv = Papa.unparse(csvRows, { quotes: true, skipEmptyLines: true });
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${description.replace(/\s+/g, '_')}.csv`;
                link.click();
                URL.revokeObjectURL(url);
              }}
            >
              <span className="relative w-10 h-10 transition-all duration-200">
                {/* Icono de archivo clásico */}
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 flex-shrink-0 transition-all duration-200 group-hover:opacity-0 absolute inset-0 m-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path d="M6 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8.828A2 2 0 0 0 19.414 7.414l-4.828-4.828A2 2 0 0 0 13.172 2H6z" stroke="currentColor" strokeWidth="2"/>
                  <path d="M14 2v6a2 2 0 0 0 2 2h6" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </span>
              <span className="max-w-0 opacity-0 group-hover:max-w-[160px] group-hover:opacity-100 transition-all duration-200 whitespace-nowrap text-sm">Download data</span>
            </button>
          </div>
        </div>

        <div id="ghg-chart-section" className="h-[600px] rounded-xl border border-[#EDF0F2] px-6 py-4">
          <ResponsiveContainer width="100%" height="100%">
            {ghgChartMode === "bar" ? (
              <BarChart
                data={chartData}
                stackOffset="sign"
                margin={{ top: 40, right: 30, left: 60, bottom: 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" />

                <XAxis
                  dataKey="year"
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  height={70}
                  tick={{ fill: "#98999A", fontSize: 12, fontWeight: 700 }}
                  tickMargin={10}
                >
                  <Label
                    value="Years"
                    position="insideBottom"
                    offset={-20}
                    style={{ fill: "#98999A", fontSize: 15 }}
                  />
                </XAxis>

                <YAxis
                  width={60}
                  tick={{ fill: "#98999A", fontSize: 12 }}
                  tickFormatter={(v) =>
                    Number(v).toLocaleString("en-US", {
                      maximumFractionDigits: 1,
                    })
                  }
                >
                  <Label
                    value={yAxisLabelGHG}
                    angle={-90}
                    position="left"
                    dx={yAxisDxGHG}
                    style={{ fill: "#98999A", fontSize: 15 }}
                  />
                </YAxis>

                <Tooltip
                  content={(props) => (
                    <CustomTooltip
                      {...props}
                      chartType="bar"
                      hiddenKeys={hiddenGHGKeys}
                    />
                  )}
                  cursor={{ fill: "rgba(0,0,0,0.03)" }}
                />

                <Legend
                  verticalAlign="top"
                  align="left"
                  wrapperStyle={{ top: 0, left: 0 }}
                  content={(props) => (
                    <GHGLegend
                      {...props}
                      hiddenKeys={hiddenGHGKeys}
                      onToggle={handleLegendClickGHG}
                      chartType="bar"
                    />
                  )}
                />

                <Bar
                  dataKey="crops"
                  stackId="ghg"
                  fill="#FFD786"
                  name="Cropland"
                  hide={hiddenGHGKeys.includes("crops")}
                />
                <Bar
                  dataKey="livestock"
                  stackId="ghg"
                  fill="#FF5579"
                  name="Livestock"
                  hide={hiddenGHGKeys.includes("livestock")}
                />
                <Bar
                  dataKey="deforestation"
                  stackId="ghg"
                  fill="#FB752E"
                  name="Deforestation"
                  hide={hiddenGHGKeys.includes("deforestation")}
                />
                <Bar
                  dataKey="otherEmissions"
                  stackId="ghg"
                  fill="#80D5FF"
                  name="Other emissions"
                  hide={hiddenGHGKeys.includes("otherEmissions")}
                />
                <Bar
                  dataKey="sequestration"
                  stackId="ghg"
                  fill="#CBF3DD"
                  name="Sequestration"
                  hide={hiddenGHGKeys.includes("sequestration")}
                />

                <Line
                  type="monotone"
                  dataKey="netAfolu"
                  name="Net Afolu"
                  stroke="transparent"
                  dot={diamondDot}
                  activeDot={diamondDot}
                  hide={hiddenGHGKeys.includes("netAfolu")}
                />
              </BarChart>
            ) : (
              <ComposedChart
                data={chartData}
                stackOffset="sign"
                margin={{ top: 40, right: 30, left: 60, bottom: 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" />

                <XAxis
                  dataKey="year"
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  height={70}
                  tick={{ fill: "#98999A", fontSize: 12, fontWeight: 700 }}
                  tickMargin={10}
                >
                  <Label
                    value="Years"
                    position="insideBottom"
                    offset={-20}
                    style={{ fill: "#98999A", fontSize: 15 }}
                  />
                </XAxis>

                <YAxis
                  width={60}
                  tick={{ fill: "#98999A", fontSize: 12 }}
                  tickFormatter={(v) =>
                    Number(v).toLocaleString("en-US", {
                      maximumFractionDigits: 1,
                    })
                  }
                >
                  <Label
                    value={yAxisLabelGHG}
                    angle={-90}
                    position="left"
                    dx={yAxisDxGHG}
                    style={{ fill: "#98999A", fontSize: 15 }}
                  />
                </YAxis>

                <Tooltip
                  content={(props) => (
                    <CustomTooltip
                      {...props}
                      chartType="area"
                      hiddenKeys={hiddenGHGKeys}
                    />
                  )}
                  cursor={{ fill: "rgba(0,0,0,0.03)" }}
                />

                <Legend
                  verticalAlign="top"
                  align="left"
                  wrapperStyle={{ top: 0, left: 0 }}
                  content={(props) => (
                    <GHGLegend
                      {...props}
                      hiddenKeys={hiddenGHGKeys}
                      onToggle={handleLegendClickGHG}
                      chartType="area"
                    />
                  )}
                />

                <Area
  type="monotone"
  dataKey="crops"
  stackId="ghg-area"
  fill="#FFD786"
  stroke="#FFD786"
  fillOpacity={isGHGHidden("crops") ? 0 : 0.7}
  strokeOpacity={isGHGHidden("crops") ? 0 : 1}
  name="Cropland"
/>

<Area
  type="monotone"
  dataKey="livestock"
  stackId="ghg-area"
  fill="#FF5579"
  stroke="#FF5579"
  fillOpacity={isGHGHidden("livestock") ? 0 : 0.7}
  strokeOpacity={isGHGHidden("livestock") ? 0 : 1}
  name="Livestock"
/>

<Area
  type="monotone"
  dataKey="deforestation"
  stackId="ghg-area"
  fill="#FB752E"
  stroke="#FB752E"
  fillOpacity={isGHGHidden("deforestation") ? 0 : 0.7}
  strokeOpacity={isGHGHidden("deforestation") ? 0 : 1}
  name="Deforestation"
/>

<Area
  type="monotone"
  dataKey="otherEmissions"
  stackId="ghg-area"
  fill="#80D5FF"
  stroke="#80D5FF"
  fillOpacity={isGHGHidden("otherEmissions") ? 0 : 0.7}
  strokeOpacity={isGHGHidden("otherEmissions") ? 0 : 1}
  name="Other emissions"
/>

<Area
  type="monotone"
  dataKey="sequestration"
  stackId="ghg-area"
  fill="#CBF3DD"
  stroke="#CBF3DD"
  fillOpacity={isGHGHidden("sequestration") ? 0 : 0.7}
  strokeOpacity={isGHGHidden("sequestration") ? 0 : 1}
  name="Sequestration"
/>

                <Line
                  type="monotone"
                  dataKey="netAfolu"
                  name="Net Afolu"
                  stroke="#0F0F0F"
                  strokeWidth={2}
                  dot={diamondDot}
                  activeDot={diamondDot}
                  hide={hiddenGHGKeys.includes("netAfolu")}
                />
              </ComposedChart>
            )}
          </ResponsiveContainer>
        </div>

        <div className="w-full min-h-[100px]">
          {loadingAnalysis ? (
            <LoadingAnalysisHelper />
          ) : (
            <div className="group relative">
              <button
                onClick={() => fetchAnalysis()}
                title="Regenerate analysis"
                className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 p-2 text-[#98999A] hover:text-[#59AD31] transition-all bg-white rounded-full shadow-sm border border-gray-100"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              <div id="desc-chart_ghg_emissions_by_source"><NativeAnalysis content={ghgAnalysis} /></div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}